import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { AdminGuard } from "./core/auth/guards/admin.guard";
import { ApiGuard } from "./core/auth/guards/api.guard";
import { EntityListGuard } from './core/auth/guards/entityList.guard';
import { SuperGuard } from "./core/auth/guards/super.guard";
import { OrgNavResolver } from "./orgnav.resolvers";
import { JobsGuard } from "./core/auth/guards/jobs.guard";
import { PortalGuard } from "./core/auth/guards/portal.guard";
import { MfaResetRequestComponent } from './modules/auth/mfa-reset-request/mfa-reset-request.component';
import { environment } from '../environments/environment';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/sign-in-email-link' on portal, '/dashboard' on hub
    { path: '', pathMatch: 'full', redirectTo: window.location.hostname === environment.portalDomain ? 'sign-in-email-link' : 'dashboard' },

    // Redirect signed-in user to the '/dashboard' (hub) or '/submissions' (portal)
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: window.location.hostname === environment.portalDomain ? 'submissions' : 'dashboard' },

    // Auth routes for all
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.module').then(m => m.AuthSignUpModule) },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.module').then(m => m.AuthForgotPasswordModule) },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule) },
            { path: 'sign-in-email-link', loadChildren: () => import('app/modules/auth/sign-in-email-link/sign-in-email-link.module').then(m => m.SignInEmailLinkModule) }
        ]
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            // {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.module').then(m => m.AuthConfirmationRequiredModule)},
            // {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule)},
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule) },
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule) }
        ]
    },
    {
        path: '',
        component: LayoutComponent,
        data: { layout: 'empty' },
        canActivate: [NoAuthGuard],
        children: [
            {
                path: 'mfa-reset',
                component: MfaResetRequestComponent
            }
        ]
    },

    // Auth routes
    {
        path: '',
        canActivate: [AuthGuard, PortalGuard],
        canActivateChild: [AuthGuard, PortalGuard],
        component: LayoutComponent,
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        resolve: {
            org: OrgNavResolver
        },
        children: [

            { path: 'organisations', canActivate: [SuperGuard], loadChildren: () => import('app/modules/organisations/organisations.module').then(m => m.OrganisationsModule) },

            { path: 'dashboard', loadChildren: () => import('app/modules/dashboard/dashboard.module').then(m => m.DashboardModule) },
            { path: 'submissions', loadChildren: () => import('app/modules/submissions/submissions.module').then(m => m.SubmissionsModule) },
            { path: 'profile', loadChildren: () => import('app/modules/profile/profile.module').then(m => m.ProfileModule) },
            { path: 'change-password', loadChildren: () => import('app/modules/auth/change-password/change-password.module').then(m => m.AuthChangePasswordModule) },
            { path: 'sms-mfa-enrollment', loadChildren: () => import('app/modules/auth/sms-mfa-enrollment/sms-mfa-enrollment.module').then(m => m.AuthSmsMfaEnrollmentModule) },
            { path: 'users', canActivate: [AdminGuard], loadChildren: () => import('app/modules/orgUsers/orgUsers.module').then(m => m.OrgUsersModule) },
            { path: 'contacts', canActivate: [AdminGuard], loadChildren: () => import('app/modules/contacts/contacts.module').then(m => m.ContactsModule) },
            { path: 'organisation', canActivate: [SuperGuard], loadChildren: () => import('app/modules/organisation/organisation.module').then(m => m.OrganisationModule) },
            { path: 'billing', canActivate: [AdminGuard], loadChildren: () => import('app/modules/billing/billing.module').then(m => m.BillingModule) },
            { path: 'forms', canActivate: [SuperGuard], loadChildren: () => import('app/modules/forms/forms.module').then(m => m.FormsModule) },
            { path: 'files/images', canActivate: [SuperGuard], loadChildren: () => import('app/modules/files/images/images.module').then(m => m.ImagesModule) },
            { path: 'files/documents', canActivate: [SuperGuard], loadChildren: () => import('app/modules/files/documents/documents.module').then(m => m.DocumentsModule) },
            { path: 'files/schematics', canActivate: [SuperGuard], loadChildren: () => import('app/modules/files/maps/maps.module').then(m => m.MapsModule) },

            { path: 'userSearch', canActivate: [SuperGuard], loadChildren: () => import('app/modules/userSearch/userSearch.module').then(m => m.UserSearchModule) },
            { path: 'tasks', canActivate: [SuperGuard], loadChildren: () => import('app/modules/tasks/tasks.module').then(m => m.TasksModule) },
            { path: 'stucktasks', canActivate: [SuperGuard], loadChildren: () => import('app/modules/stuckTasks/stuckTasks.module').then(m => m.StuckTasksModule) },
            { path: 'form-builder', canActivate: [SuperGuard], loadChildren: () => import('app/modules/form-builder/form-builder.module').then(m => m.FormBuilderModule) },

            { path: 'resources/datasets', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/datasets/datasets.module').then(m => m.DatasetsModule) },
            { path: 'resources/headers', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/headers/headers.module').then(m => m.HeadersModule) },
            { path: 'resources/styles', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/styles/styles.module').then(m => m.StylesModule) },
            { path: 'resources/distribution', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/distlists/distlists.module').then(m => m.DistlistsModule) },
            { path: 'resources/processes', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/processes/processes.module').then(m => m.ProcessesModule) },
            { path: 'resources/workflows', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/workflows/workflows.module').then(m => m.WorkflowsModule) },
            {
                path: 'resources/report-definitions', canActivate: [SuperGuard], loadChildren: () => import('app/modules/resources/report-definitions/report-definitions.module').then(m => m.ReportDefinitionsModule)
            },

            { path: 'jobs', canActivate: [JobsGuard], loadChildren: () => import('app/modules/jobs-system/jobs-system.module').then(m => m.JobsSystemModule) },

            { path: 'entities', canActivate: [EntityListGuard], loadChildren: () => import('app/modules/entities/entities.module').then(m => m.EntitiesModule) },
            { path: 'entitytypes', canActivate: [SuperGuard], loadChildren: () => import('app/modules/entitytypes/entitytypes.module').then(m => m.EntitytypesModule) },
            { path: 'entitycategories', canActivate: [SuperGuard], loadChildren: () => import('app/modules/entitycategories/entitycategories.module').then(m => m.EntitycategoriesModule) },
            { path: 'entityconditionrules', canActivate: [SuperGuard], loadChildren: () => import('app/modules/entityconditionrules/entityconditionrules.module').then(m => m.EntityconditionrulesModule) },

            // { path: 'entitynav', canActivate: [SuperGuard], loadChildren: () => import('app/modules/entitynav/entitynav.module').then(m => m.EntitynavModule) },

            { path: 'bin', canActivate: [AdminGuard], loadChildren: () => import('app/modules/bin/bin.module').then(m => m.RubbishBinModule) },

            { path: 'api', canActivate: [ApiGuard], loadChildren: () => import('app/modules/api/api.module').then(m => m.ApiModule) },

            { path: 'recurring-jobs', canActivate: [AdminGuard], loadChildren: () => import('app/modules/recurring-jobs/recurring-jobs.module').then(m => m.RecurringJobsModule) },

            { path: 'control-panel', canActivate: [SuperGuard], loadChildren: () => import('app/modules/panel-control/controls-panel.module').then(m => m.ControlPanelModule) },

            { path: 'forms-editor', canActivate: [SuperGuard], loadChildren: () => import('app/modules/forms-editor/forms-editor.module').then(m => m.FormsEditorModule) },
            // Pages
            {
                path: 'pages', children: [

                    // // Coming Soon
                    // {path: 'coming-soon', loadChildren: () => import('app/modules/admin/pages/coming-soon/coming-soon.module').then(m => m.ComingSoonModule)},
                    //
                    // Error
                    {
                        path: 'error', children: [
                            { path: '404', loadChildren: () => import('app/modules/pages/error/error-404/error-404.module').then(m => m.Error404Module) },
                            { path: '500', loadChildren: () => import('app/modules/pages/error/error-500/error-500.module').then(m => m.Error500Module) }
                        ]
                    },

                    // // Maintenance
                    // {path: 'maintenance', loadChildren: () => import('app/modules/admin/pages/maintenance/maintenance.module').then(m => m.MaintenanceModule)},

                    // // Settings
                    // {path: 'settings', loadChildren: () => import('app/modules/admin/pages/settings/settings.module').then(m => m.SettingsModule)},
                ]
            },

            // 404 & Catch all
            { path: '404-not-found', pathMatch: 'full', loadChildren: () => import('app/modules/pages/error/error-404/error-404.module').then(m => m.Error404Module) },
            { path: '**', redirectTo: '404-not-found' }
        ]
    }
];
